import {
  Box,
  FormControl,
  FormLabel,
  Image,
  Input,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import {
  CustomDrawerBackButton,
  CustomDrawerFooter,
  CustomDrawerHeader,
  CustomFormErrorMessage,
  PrimaryButton,
} from '../../../../components/custom';
import CustomDropdown from '../../../../components/custom/CustomDropdown/CustomDropdown';
import { InputGroupWithLoader } from '../../../../components/custom/CustomInputs';
import TransactionReceipt from '../../../../components/custom/TransactionReceipt';
import { handleNumericInput } from '../../../../constants/validationConstants';
import {
  currencyFormatWithDecimals,
  currencyUnformat,
} from '../../../../helpers/utilityFunctions';
import useGetUIErrorMessage from '../../../../hooks/useGetUIErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getBillTransactionsAsync } from '../../../../store/slices/billsPayment/billTransactionsSlice';
import {
  customerLookupAsync,
  resetCustomerLookup,
} from '../../../../store/slices/billsPayment/customerLookupSlice';
import { IGetBillers } from '../../../../store/slices/billsPayment/getBillersCategorySlice';
import {
  payBillAsync,
  resetPayBill,
} from '../../../../store/slices/billsPayment/payBillSlice';
import CreateTransactionPin from '../../../TransactionPin/CreateTransactionPin';
import { TransactionPinForm } from '../TransactionPinForm';
import TVPurchaseSummary from './TVPurchaseSummary';

interface IBuySubscriptionFormProps {
  providers: IGetBillers[];
  selectedBillCategory: string;
  initialBillerName?: string;
  initialCustomerId?: string;
}

export const BuySubscriptionForm = ({
  providers,
  selectedBillCategory,
  initialBillerName,
  initialCustomerId,
}: IBuySubscriptionFormProps) => {
  const dispatch = useAppDispatch();

  const [page] = useState(1);
  const [limit] = useState(10);

  const [billerId, setBillerId] = useState('');
  const [, setNumericValue] = useState<number | null>(null);

  const [isFixedPrice, setIsFixedPrice] = useState(false);

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  const [step, setStep] = useState(0);

  const [amountLimitError] = useState('');

  const [insufficientBalanceError, setInsufficientBalanceError] = useState('');

  const { value } = useAppSelector((state) => state.auth);
  const { value: accountNumber } = useAppSelector(
    (state) => state.accountBalance
  );
  const { status: lookupStatus, value: lookupValue } = useAppSelector(
    (state) => state.customerLookup
  );

  const { value: acctBalValue } = useAppSelector(
    (state) => state.accountBalance
  );

  const { value: outletsValue } = useAppSelector((state) => state.getOutlets);

  const availableBalance = acctBalValue?.data?.availableBalance;
  const availableBalanceInNaira = availableBalance ? availableBalance / 100 : 0;

  const getMappedProviderForDropdown = (networkName: string) => {
    const networkOption = providerOptions.find(
      (option) => option.name === networkName
    );
    return {
      name: networkName,
      icon: networkOption ? <Image src={networkOption.icon} alt="" /> : null,
    };
  };

  useEffect(() => {
    dispatch(resetPayBill());
  }, [dispatch]);

  const providerOptions = useMemo(() => {
    return providers
      ?.filter((provider) => provider.name?.toLowerCase())
      .map((provider) => ({
        id: provider.id || '',
        name: provider.name || '',
        icon: provider.imageUrl || '',
      }));
  }, [providers]);

  const initialValues = {
    providerName: initialBillerName ?? '',
    packageName: '',
    customerId: initialCustomerId || '',
    customerName: '',
    amount: '',
  };

  const validationSchema = Yup.object().shape({
    providerName: Yup.string().required('Provider is required'),
    packageName: Yup.string().required('Package is required'),
    customerId: Yup.string().required('Smartcard number is required'),
    customerName: Yup.string().required('Customer name is required'),
    amount: Yup.string().required('Amount is required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Do nothing
    },
  });

  const {
    errors,
    touched,
    values,
    isValid,
    getFieldProps,
    setFieldValue,
    handleSubmit,
  } = formik;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || (/^\+?\d*$/.test(value) && value.length <= 14)) {
      setFieldValue('customerId', value);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = currencyUnformat(inputValue);

    setNumericValue(numericValue);

    if (numericValue === 0) {
      setFieldValue('amount', '');
      return;
    }

    const formattedValue = currencyFormatWithDecimals(numericValue);

    setFieldValue('amount', formattedValue);
  };

  const customerName = values.customerName;
  const customerAccountNo = accountNumber?.data?.accountNumber;
  const accountNameFromStorage = localStorage.getItem('accountName');

  const outletId =
    outletsValue?.data?.find(
      (outlet) =>
        outlet?.account?.accountNumber === customerAccountNo &&
        outlet?.account?.accountName === accountNameFromStorage
    )?.id ?? '';

  const packages = useMemo(
    () =>
      providers?.find((n) => n.name === values.providerName)
        ?.currentProviderBillerItems || [],
    [providers, values.providerName]
  );

  const packageNames = packages.map((packageName) => packageName.name);

  const sortedPackageNames = packageNames.sort();

  const handleFinalSubmit = async (pinValues: { pin: string }) => {
    const selectedPackage = packages.find((b) => b.name === values.packageName);
    const response = await dispatch(
      payBillAsync({
        outletId,
        amount: Number(values.amount),
        billCategory: 'CABLE',
        customerId: values.customerId || '',
        billItemId: selectedPackage?.id || '',
        customerAccountNo: customerAccountNo || 0,
        customerName: customerName,
        phoneNumber: value?.data?.user?.phoneNumber,
        pin: pinValues.pin || '',
        emailAddress: value?.data?.user?.email,
        billProvider: values.providerName,
      })
    );
    // lookupDispatch(resetCustomerLookup());

    if (response?.meta?.requestStatus === 'fulfilled') {
      dispatch(
        getBillTransactionsAsync({
          accountNo: customerAccountNo,
          category: selectedBillCategory.toUpperCase(),
          page,
          limit,
        })
      );
    }
  };

  useEffect(() => {
    const selectedPackage = packages.find((b) => b.name === values.packageName);
    if (values.customerId.length === 11) {
      dispatch(resetCustomerLookup());

      dispatch(
        customerLookupAsync({
          customerId: values.customerId,
          billerId: billerId || '',
          billerItemId: selectedPackage?.id ?? '',
        })
      );
    }
  }, [
    values.customerId,
    values.providerName,
    dispatch,
    billerId,
    values.packageName,
    packages,
  ]);

  const handleCustomerIdValidation = () => {
    const selectedPackage = packages.find((b) => b.name === values.packageName);

    if (values.customerId.length > 0) {
      dispatch(resetCustomerLookup());

      dispatch(
        customerLookupAsync({
          customerId: values.customerId,
          billerId: billerId || '',
          billerItemId: selectedPackage?.id ?? '',
        })
      );
    }
  };

  const lookupErrorMessage = useGetUIErrorMessage({
    message: lookupValue?.message,
    errors: lookupValue?.errors,
  });

  useEffect(() => {
    if (lookupStatus === 'success' && lookupValue?.data?.name) {
      setFieldValue('customerName', lookupValue?.data?.name);
      formik.validateField('customerName');
    } else if (lookupStatus === 'failed') {
      setFieldValue('customerName', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupStatus, lookupValue?.data?.name]);

  const { status: paymentStatus, value: paymentValue } = useAppSelector(
    (state) => state.billsPayment
  );

  useEffect(() => {
    if (values.packageName) {
      setIsFixedPrice(false);
      const selectedPackage = packages.find(
        (b) => b.name === values.packageName
      );
      if (selectedPackage?.isFixedPrice) {
        setFieldValue('amount', selectedPackage?.amount?.toString());
        setIsFixedPrice(true);
      }
      setFieldValue('packageId', selectedPackage?.id || '');
    }
  }, [packages, setFieldValue, values.packageName]);

  const handleGoForward = () => {
    if (packages.length > 0 && isValid) {
      const selectedPackage = packages.find(
        (b) => b.name === values.packageName
      );
      const packageAmount = Number(selectedPackage?.amount || 0);

      if (packageAmount <= availableBalanceInNaira) {
        setStep((prevStep) => prevStep + 1);
      } else {
        setInsufficientBalanceError(
          'Insufficient balance to purchase this bundle.'
        );
      }
    }
  };

  useEffect(() => {
    if (values.providerName) {
      const provider = providerOptions.find(
        (option) => option.name === values.providerName
      );
      setBillerId(provider?.id ?? '');
    }
  }, [providerOptions, values.providerName]);

  const handleGoBack = () => {
    setStep((currentStep) => currentStep - 1);
  };

  const billDetails = {
    customerName: customerName,
    customerId: values.customerId ?? '',
    billCategory: 'CABLE',
    billItemId: packages.find((b) => b.name === values.providerName)?.id ?? '',
    billerName: values.providerName,
    billerId: billerId ?? '',
  };

  const renderStep = () => {
    const steps = [
      <Box display="flex" flexDirection="column" py="18px">
        <CustomDrawerHeader startsFromBeginning>TV</CustomDrawerHeader>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={8} flex="1">
              <Box mb={6}>
                <Text fontWeight="500" color="#000" mb={1}>
                  Recipient Information
                </Text>
                <Text color="gray.300" fontSize=".875rem">
                  Streamline transactions with seamless information management
                </Text>
              </Box>

              <FormControl
                id="providerName"
                isInvalid={Boolean(touched.providerName && errors.providerName)}
              >
                <FormLabel>Provider</FormLabel>
                <CustomDropdown
                  options={providerOptions || []}
                  placeholder="Select an option"
                  getMappedNetwork={getMappedProviderForDropdown}
                  setFieldValue={setFieldValue}
                  value={values.providerName}
                  fieldValue="providerName"
                />
                <CustomFormErrorMessage>
                  {touched.providerName && errors.providerName}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="packageName"
                isInvalid={Boolean(touched.packageName && errors.packageName)}
              >
                <FormLabel>Package</FormLabel>
                <Select
                  disabled={!sortedPackageNames?.length}
                  placeholder="Select an option"
                  isInvalid={Boolean(touched.packageName && errors.packageName)}
                  {...getFieldProps('packageName')}
                  onChange={(e) => {
                    const selectedPackageName = e.target.value;
                    const selectedPackage = packages.find(
                      (packageName) => packageName.name === selectedPackageName
                    );

                    setFieldValue('packageName', selectedPackageName);
                    setFieldValue('packageId', selectedPackage?.id || '');
                  }}
                  textTransform="capitalize"
                >
                  {sortedPackageNames?.map((selectedPackageName, index) => (
                    <option key={index} value={selectedPackageName}>
                      {selectedPackageName}
                    </option>
                  ))}
                </Select>
                <CustomFormErrorMessage>
                  {touched.packageName && errors.packageName}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="customerId"
                isInvalid={
                  Boolean(touched.customerId && errors.customerId) ||
                  (lookupStatus === 'failed' && !!lookupErrorMessage)
                }
              >
                <FormLabel>Customer ID / Smart Card Number</FormLabel>
                <InputGroupWithLoader isLoading={lookupStatus === 'loading'}>
                  <Input
                    {...getFieldProps('customerId')}
                    as="textarea"
                    placeholder="36728392732"
                    sx={{
                      pt: 4,
                    }}
                    onChange={handlePhoneNumberChange}
                    onKeyDown={(e) => handleNumericInput(e, 11)}
                    maxLength={11}
                    onBlur={handleCustomerIdValidation}
                    isInvalid={
                      Boolean(touched.customerId && errors.customerId) ||
                      (lookupStatus === 'failed' && !!lookupErrorMessage)
                    }
                  />
                </InputGroupWithLoader>
                <CustomFormErrorMessage>
                  {(touched.customerId && errors.customerId) ||
                    lookupErrorMessage}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="customerName"
                isInvalid={Boolean(touched.customerName && errors.customerName)}
              >
                <FormLabel>Customer Name</FormLabel>
                <Input
                  isDisabled
                  {...getFieldProps('customerName')}
                  as="textarea"
                  placeholder="Your account name"
                  value={lookupValue?.data?.name ?? ''}
                  isInvalid={Boolean(
                    touched.customerName && errors.customerName
                  )}
                  sx={{
                    pt: 4,
                  }}
                />
                <CustomFormErrorMessage>
                  {touched.customerName && errors.customerName}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="amount"
                isInvalid={Boolean(
                  touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)
                )}
              >
                <FormLabel>Amount</FormLabel>
                <Input
                  disabled={isFixedPrice || !values.packageName}
                  {...getFieldProps('amount')}
                  value={
                    isFixedPrice
                      ? Number(values.amount).toLocaleString()
                      : values.amount
                  }
                  as="textarea"
                  placeholder="Enter amount"
                  onChange={handleAmountChange}
                  isInvalid={Boolean(
                    touched.amount &&
                      (insufficientBalanceError ||
                        amountLimitError ||
                        errors.amount)
                  )}
                  sx={{
                    pt: 4,
                  }}
                />
                <CustomFormErrorMessage>
                  {touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)}
                </CustomFormErrorMessage>
              </FormControl>
            </Stack>

            <CustomDrawerFooter>
              <PrimaryButton
                onClick={handleGoForward}
                type="submit"
                w="full"
                isDisabled={Boolean(insufficientBalanceError)}
              >
                Continue
              </PrimaryButton>
            </CustomDrawerFooter>
          </Form>
        </FormikProvider>
      </Box>,
      <>
        <CustomDrawerBackButton onClick={handleGoBack} />
        <TVPurchaseSummary
          summary={{
            providerName: values.providerName,
            packageName: values.packageName,
            customerId: values.customerId,
            customerName: values.customerName,
            amount: Number(values.amount).toLocaleString(),
            date: new Date(),
          }}
          handleSubmit={handleGoForward}
        />
      </>,
      <>
        <TransactionPinForm
          isSuccess={paymentStatus === 'success'}
          isLoading={paymentStatus === 'loading'}
          isError={paymentStatus === 'failed'}
          onSubmit={handleFinalSubmit}
          goToReceipt={handleGoForward}
          step={step}
          setStep={(currentStep) => setStep(currentStep)}
          statusBoxTitle={paymentValue?.message}
          statusBoxText=""
          billDetails={billDetails}
          twoFactor={{
            type: 'bill_payment',
            amountKobo: Math.round(currencyUnformat(values.amount) * 100),
          }}
        />
      </>,
      <TransactionReceipt
        date={new Date().toDateString()}
        amount={currencyUnformat(values.amount)}
        transactionType="Bill Payment"
        transactionReference={'----'}
        senderName={accountNameFromStorage ?? customerName}
        narration="test"
        provider={values.providerName}
        device={values.customerId}
        transactionFee="₦0.00"
        senderAccountNumber={customerAccountNo}
        showAmountInWords={false}
      />,
    ];

    return <>{steps[step]}</>;
  };

  return (
    <div>
      {!userHasPin ? (
        <CreateTransactionPin setUserHasPin={setUserHasPin} />
      ) : (
        renderStep()
      )}
    </div>
  );
};
